import React from "react";
import ReactHtmlParser from "react-html-parser";

import uuid from "react-native-uuid";
import { convertToRaw } from "draft-js";
import HTMLtoDraft from "./HTMLtoDraft/HTMLtoDraft";

export const normaliseCellData = cell => {
  if (typeof cell === "string" || typeof cell === "boolean") {
    return {
      attributes: {
        id: uuid.v4(),
      },
      data: cell,
    };
  }
  if (typeof cell === "number") {
    return {
      attributes: {
        id: uuid.v4(),
      },
      data: cell.toString(10),
    };
  }
  if (Array.isArray(cell) && cell.length > 0) {
    return {
      attributes: {
        id: uuid.v4(),
      },
      data: cell.join(","),
    };
  }
  if ("html" in cell) {
    const contentState = HTMLtoDraft(cell.html);
    // For the first time the document is uploaded an html key is sent. This has to be converted to editostate.
    return {
      attributes: {
        id: uuid.v4(),
        ...cell.attributes,
      },
      data: {
        data: convertToRaw(contentState),
      },
    };
  }
  if (cell instanceof Object) {
    const { attributes, data, text, value, metadata: rowMetaData } = cell;
    const metadata = attributes || rowMetaData || {};
    let cellValue = text || value || data;
    if (metadata.type === "checkbox") {
      cellValue = Boolean(cellValue);
    } else {
      cellValue = cellValue || "";
    }
    metadata.id = metadata.id || uuid.v4();
    return {
      attributes: metadata,
      data: cellValue,
    };
  }
  return cell;
};

export const normaliseRowData = row => {
  let rowData = [];
  let metadata = {};
  if (Array.isArray(row)) {
    rowData = row;
  } else {
    const { attributes, data, metadata: rowMetaData } = row;
    rowData = data;
    metadata = attributes || rowMetaData || {};
  }
  const data = [];
  rowData.forEach(cell => data.push(normaliseCellData(cell)));

  // get the number of columns for this row
  metadata.columnCount = rowData.length;
  metadata.id = metadata.id || uuid.v4();
  return {
    attributes: metadata,
    data,
  };
};

export const tableAdapterConversion = tableData => {
  let editable = true;
  let readOnly = false;
  const showDataSourceOption = true;
  let isLinkedToDataSource = false;
  const showColorStyles = true;
  let showRowOptions = true;
  let showColumnOptions = true;
  const isSelectionAllowed = true;
  let showManageColumnOption = false;
  let showGSheetLable = false;
  const className = "revv-table";
  const colResize = true;
  const { attributes } = tableData;
  const columnWidth =
    attributes && attributes.columnWidth
      ? [...attributes.columnWidth]
      : ["100px", "100px"];
  if (
    attributes.dataSource &&
    attributes.dataSource !== "" &&
    attributes.dataSource === "GoogleSheet"
  ) {
    showRowOptions = false;
    showColumnOptions = false;
    showGSheetLable = true;
    readOnly = true;
    isLinkedToDataSource = true;
    editable = false;
    showManageColumnOption = true;
  }

  return {
    ...tableData,
    attributes: {
      ...attributes,
      editable,
      readOnly,
      showDataSourceOption,
      isLinkedToDataSource,
      showColorStyles,
      showRowOptions,
      showColumnOptions,
      isSelectionAllowed,
      showManageColumnOption,
      showGSheetLable,
      className,
      colResize,
      columnWidth,
    },
  };
};

export const normaliseTableData = (table, type, hiddenCols) => {
  let tableData = [];
  let metadata = {};
  let tableColDefs = [];
  if (Array.isArray(table)) {
    tableData = table;
  } else {
    const { attributes, data, metadata: tableMetaData } = table;
    const { tableColumnDefs } = attributes;
    tableData = data;
    metadata = attributes || tableMetaData || {};
    tableColDefs = tableColumnDefs || [];
  }

  let data = [];
  let columns = 0;
  if (type === "gSheet") {
    // in case of gsheet avoid normaliseRowData()
    data = tableData;
    columns = metadata.columnCount;
  } else {
    tableData.forEach(row => data.push(normaliseRowData(row)));
    columns =
      Array.isArray(data) && data.length > 0
        ? Math.max(
            ...data.map(row => {
              const { attributes } = row;
              return attributes.columnCount;
            }),
          )
        : 0;
  }
  metadata.rowCount = data.length;
  metadata.columnCount = columns;
  metadata.columnWidth = metadata.columnWidth || [];
  metadata.hiddenCols = hiddenCols || [];
  metadata.tableColumnDefs = tableColDefs || [];

  if (columns !== 0) {
    metadata.columnWidth = [...Array(columns)].map((_, i) =>
      metadata.columnWidth[i] ? metadata.columnWidth[i] : "100px",
    );
  }

  // get table options flags
  let normalisedTableData = {
    attributes: metadata,
    data,
  };
  normalisedTableData = tableAdapterConversion(normalisedTableData);
  return normalisedTableData;
};

const renderGSheetCell = (cellDataList, hiddenCols, isHeader) =>
  cellDataList.map((cellData, index) => {
    const hdnCols = hiddenCols || [];
    if (!hdnCols.includes(index)) {
      if (isHeader) {
        return <th>{ReactHtmlParser(cellData.data.html)}</th>;
      }
      return <td>{ReactHtmlParser(cellData.data.html)}</td>;
    }
    return null;
  });

export const renderGSheetTable = (rowDataList, hiddenCols) => {
  let hasHeaders = false;
  if (rowDataList.length > 0) hasHeaders = rowDataList[0].attributes.header;
  const tableView = [];
  let tableHeaders = [];
  let tableBody = [];
  if (hasHeaders) {
    tableHeaders = (
      <tr>{renderGSheetCell(rowDataList[0].data, hiddenCols, true)}</tr>
    );
    tableHeaders = <thead>{tableHeaders}</thead>;
  }
  tableBody = rowDataList.map((rowData, index) => {
    if (hasHeaders && index === 0) {
      return null;
    }
    return <tr>{renderGSheetCell(rowData.data, hiddenCols)}</tr>;
  });
  tableBody = <tbody>{tableBody}</tbody>;
  if (hasHeaders) {
    tableView.push(tableHeaders);
    tableView.push(tableBody);
  } else {
    tableView.push(tableBody);
  }
  const table = <table className="google-sheet-table">{tableView}</table>;
  return table;
};

export const checkColVals = (cols, maxLimit) => {
  const fo = cols.map(i => {
    if (i < 0) return 0;
    if (i > maxLimit) return maxLimit;
    return i;
  });
  return fo;
};

// export const checkSelectionPossible = (
//   direction,
//   cellNeighbours,
//   selectedCols,
//   tableRowId,
// ) => {
//   const sortedSelection = selectedCols.sort();
//   const currLastSelectedCell = [tableRowId, sortedSelection[1]];
//   console.debug(sortedSelection);
//   console.debug(currLastSelectedCell);
//   console.debug(cellNeighbours);
//   const { left, right } =
//     cellNeighbours[`${currLastSelectedCell[0]}-${currLastSelectedCell[1]}`];

//   if (direction === "ArrowRight") {
//     // console.debug(currLastSelectedCell);
//     if (tableRowId === right[0] && currLastSelectedCell[1] !== right[1]) {
//       console.debug("Right");
//       return true;
//     }
//   }
//   if (direction === "ArrowLeft") {
//     // console.debug(currLastSelectedCell);
//     if (tableRowId === left[0] && currLastSelectedCell[1] !== left[1]) {
//       console.debug("Left");
//       return true;
//     }
//   }
//   console.debug("FalseFalse");
//   return false;
// };
